import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { X, CheckCircle2, Clock, Play, Square, Loader2, User, Globe, Layers, Volume2, ShieldCheck, Users, Filter } from "lucide-react";
import { iotControlData as tvControlData, translations as tvTranslations } from "../data/NewControlData";
import { ServerUrl } from "../config";

function UtteranceTrackerModal({ isOpen, onClose, targetUserEmail = "", initialLanguage = "" }) {
  const userRole = localStorage.getItem("role") || "user";
  const isSuper = userRole === "superadmin";

  const [audioDetails, setAudioDetails] = useState([]);
  const [userList, setUserList] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  // Filter States - Default empty to require explicit user selection
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedUser, setSelectedUser] = useState(targetUserEmail || "");
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage || "");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedVoiceControl, setSelectedVoiceControl] = useState("");

  // Audio Playback State
  const [loadingAudioId, setLoadingAudioId] = useState(null);
  const [activeAudio, setActiveAudio] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);

  const languagesList = [
    { key: "spanish", label: "Spanish (Español)" },
    { key: "english", label: "English" },
    { key: "french", label: "French (Français)" },
    { key: "german", label: "German (Deutsch)" },
    { key: "italian", label: "Italian (Italiano)" },
    { key: "dutch", label: "Dutch (Nederlands)" },
  ];

  const categories = useMemo(() => Object.keys(tvControlData), []);

  useEffect(() => {
    if (isOpen) {
      fetchAudioDetails();
      fetchUsersList();
      setSelectedUser(targetUserEmail || "");
      setSelectedLanguage(initialLanguage || "");
      setSelectedAdmin("");
      setSelectedCategory("");
      setSelectedVoiceControl("");
    }
  }, [isOpen, targetUserEmail, initialLanguage]);

  // When selectedCategory changes, reset selectedVoiceControl
  useEffect(() => {
    setSelectedVoiceControl("");
  }, [selectedCategory]);

  useEffect(() => {
    if (!isOpen) {
      setAudioDetails([]);
      setSelectedAdmin("");
      setSelectedUser("");
      setSelectedLanguage("");
      setSelectedCategory("");
      setSelectedVoiceControl("");
      if (activeAudio) {
        activeAudio.pause();
        setActiveAudio(null);
      }
      setPlayingAudioId(null);
    }
  }, [isOpen]);

  const fetchAudioDetails = async () => {
    setIsFetching(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}audios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setAudioDetails(response.data.data || []);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setAudioDetails([]);
      } else {
        console.error("Failed to fetch audio details:", error);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const fetchUsersList = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setUserList(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch user list:", error);
    }
  };

  // Translation helper
  const getTranslatedLabel = (key, type) => {
    if (!key) return "";
    const translationMap = type === "category" ? tvTranslations.categories : tvTranslations.voiceControls;
    const targetLang = selectedLanguage || "spanish";
    return translationMap[key]?.[targetLang] || translationMap[key]?.["spanish"] || translationMap[key]?.["english"] || key;
  };

  // Extract Admins list for SuperAdmin
  const adminUsersList = useMemo(() => {
    return userList.filter((u) => u.role === "admin");
  }, [userList]);

  // Extract users created under selectedAdmin (for SuperAdmin) or current Admin
  const availableUsersList = useMemo(() => {
    if (isSuper) {
      if (selectedAdmin) {
        return userList.filter((u) => u.createdBy === selectedAdmin);
      }
      return userList;
    }
    return userList;
  }, [userList, isSuper, selectedAdmin]);

  // Filter uploaded audios for selected Admin & User & Language
  const filteredAudios = useMemo(() => {
    return audioDetails.filter((item) => {
      const uploader = (item.uploaderEmail || item.email || "").toLowerCase().trim();

      // 1. SuperAdmin filter
      if (isSuper && selectedAdmin) {
        const usersUnderAdmin = userList
          .filter((u) => u.createdBy === selectedAdmin)
          .map((u) => u.email.toLowerCase().trim());

        const matchesAdminTeam = usersUnderAdmin.includes(uploader) || uploader === selectedAdmin.toLowerCase().trim();
        if (!matchesAdminTeam) return false;
      }

      // 2. Specific User filter
      if (selectedUser) {
        if (uploader !== selectedUser.toLowerCase().trim()) return false;
      }

      return true;
    });
  }, [audioDetails, isSuper, selectedAdmin, selectedUser, userList]);

  // Calculate Language stats
  const getLanguageStats = (langKey) => {
    let total = 0;
    categories.forEach((cat) => {
      Object.keys(tvControlData[cat]).forEach((vc) => {
        const langMap = tvControlData[cat][vc];
        const list = langMap[langKey] || langMap["spanish"] || langMap["english"] || [];
        total += list.length;
      });
    });

    const uploaded = filteredAudios.filter(
      (a) => a.audioLanguage && a.audioLanguage.toLowerCase() === langKey.toLowerCase()
    ).length;

    return { uploaded, total, pending: Math.max(0, total - uploaded) };
  };

  // Compute Category stats
  const getCategoryStats = (catKey) => {
    if (!selectedLanguage) return { uploaded: 0, total: 0, pending: 0 };
    let total = 0;
    const vcs = Object.keys(tvControlData[catKey] || {});
    vcs.forEach((vc) => {
      const langMap = tvControlData[catKey][vc];
      const list = langMap[selectedLanguage] || langMap["spanish"] || langMap["english"] || [];
      total += list.length;
    });

    const uploaded = filteredAudios.filter((a) => {
      const matchesLang = a.audioLanguage && a.audioLanguage.toLowerCase() === selectedLanguage.toLowerCase();
      const matchesCat = vcs.includes(a.audiotext);
      return matchesLang && matchesCat;
    }).length;

    return { uploaded, total, pending: Math.max(0, total - uploaded) };
  };

  // Compute Voice Control stats
  const getVoiceControlStats = (vcKey) => {
    if (!selectedCategory || !selectedLanguage || !tvControlData[selectedCategory] || !tvControlData[selectedCategory][vcKey]) {
      return { uploaded: 0, total: 0, pending: 0 };
    }
    const langMap = tvControlData[selectedCategory][vcKey];
    const utterances = langMap[selectedLanguage] || langMap["spanish"] || langMap["english"] || [];
    const total = utterances.length;

    let uploadedCount = 0;
    utterances.forEach((uText) => {
      const exists = filteredAudios.some(
        (a) =>
          a.audioLanguage?.toLowerCase() === selectedLanguage.toLowerCase() &&
          a.audiotext === vcKey &&
          (a.sampleUtterance === uText || a.userInput === uText)
      );
      if (exists) uploadedCount++;
    });

    return { uploaded: uploadedCount, total, pending: Math.max(0, total - uploadedCount) };
  };

  // Sample Utterances List for selected Voice Control
  const currentUtterancesList = useMemo(() => {
    if (!selectedLanguage || !selectedCategory || !selectedVoiceControl) return [];
    if (!tvControlData[selectedCategory] || !tvControlData[selectedCategory][selectedVoiceControl]) return [];

    const langMap = tvControlData[selectedCategory][selectedVoiceControl];
    const utterances = langMap[selectedLanguage] || langMap["spanish"] || langMap["english"] || [];

    return utterances.map((uText, idx) => {
      const uploadedItem = filteredAudios.find(
        (a) =>
          a.audioLanguage?.toLowerCase() === selectedLanguage.toLowerCase() &&
          a.audiotext === selectedVoiceControl &&
          (a.sampleUtterance === uText || a.userInput === uText)
      );

      return {
        id: `${selectedCategory}-${selectedVoiceControl}-${idx}`,
        index: idx + 1,
        utterance: uText,
        isUploaded: !!uploadedItem,
        audioData: uploadedItem || null
      };
    });
  }, [selectedLanguage, selectedCategory, selectedVoiceControl, filteredAudios]);

  // Current Voice Control stats
  const currentVcStats = useMemo(() => {
    const total = currentUtterancesList.length;
    const uploaded = currentUtterancesList.filter((u) => u.isUploaded).length;
    const pending = total - uploaded;
    const percent = total > 0 ? Math.round((uploaded / total) * 100) : 0;
    return { uploaded, pending, total, percent };
  }, [currentUtterancesList]);

  // Language overall stats
  const langOverallStats = useMemo(() => {
    if (!selectedLanguage) return { uploaded: 0, total: 0, pending: 0 };
    return getLanguageStats(selectedLanguage);
  }, [selectedLanguage, filteredAudios]);

  // Audio Playback
  const handlePlayAudio = async (fileName, audioId) => {
    if (activeAudio) {
      activeAudio.pause();
      if (playingAudioId === audioId) {
        setActiveAudio(null);
        setPlayingAudioId(null);
        return;
      }
    }

    setLoadingAudioId(audioId);
    try {
      const response = await axios.post(
        `${ServerUrl}stream_audio`,
        { file: fileName },
        { responseType: "blob" }
      );
      const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        setPlayingAudioId(null);
        setActiveAudio(null);
      };

      setActiveAudio(audio);
      setPlayingAudioId(audioId);
      audio.play();
    } catch (error) {
      Swal.fire("Error", "Failed to load audio: " + error.message, "error");
    } finally {
      setLoadingAudioId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
        
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-3.5 border-b border-slate-200 bg-slate-900 text-white shrink-0">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              {isSuper ? <ShieldCheck size={20} className="text-yellow-400" /> : <Users size={20} className="text-blue-400" />}
              {isSuper ? "Super Admin - Admin Tracking Dashboard" : "Admin Progress Tracking Dashboard"}
            </h2>
            <p className="text-xs text-slate-300">
              Select Language ➔ Category ➔ Voice Control from dropdowns to view progress
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded-md transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* DEPENDENT DROPDOWNS PANEL */}
        <div className="p-5 bg-slate-50 border-b border-slate-200 space-y-4 shrink-0">
          
          {/* Row 1: Admin / User Filter & Language Dropdown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* SuperAdmin Admin Select OR Admin User Select */}
            {isSuper ? (
              <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
                <label className="flex items-center text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                  <ShieldCheck size={14} className="mr-1.5 text-yellow-600" />
                  1. Filter Admin Account
                </label>
                <select
                  value={selectedAdmin}
                  onChange={(e) => {
                    setSelectedAdmin(e.target.value);
                    setSelectedUser("");
                  }}
                  className="w-full px-3 py-2 text-xs sm:text-sm font-semibold text-slate-900 border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-yellow-500 focus:outline-none cursor-pointer"
                >
                  <option value="">-- All Admin Accounts (System) --</option>
                  {adminUsersList.map((a) => {
                    const usersUnderThisAdmin = userList.filter((u) => u.createdBy === a.email).length;
                    return (
                      <option key={a._id} value={a.email}>
                        Admin: {a.email} ({usersUnderThisAdmin} Registered Users)
                      </option>
                    );
                  })}
                </select>
              </div>
            ) : (
              <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
                <label className="flex items-center text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                  <User size={14} className="mr-1.5 text-blue-600" />
                  1. Filter User Account
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2 text-xs sm:text-sm font-semibold text-slate-900 border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                >
                  <option value="">-- All My Users (Combined) --</option>
                  {availableUsersList.map((u) => (
                    <option key={u._id} value={u.email}>
                      {u.email} ({u.role})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Language Selection */}
            <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
              <label className="flex items-center text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                <Globe size={14} className="mr-1.5 text-blue-600" />
                2. Select Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full px-3 py-2 text-xs sm:text-sm font-semibold text-slate-900 border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
              >
                <option value="">-- Select Language --</option>
                {languagesList.map((l) => {
                  const s = getLanguageStats(l.key);
                  return (
                    <option key={l.key} value={l.key}>
                      {l.label} — ({s.uploaded} / {s.total} Uploaded)
                    </option>
                  );
                })}
              </select>
            </div>

          </div>

          {/* Row 2: Category & User Drilldown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* SuperAdmin Specific User Drilldown */}
            {isSuper ? (
              <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
                <label className="flex items-center text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                  <User size={14} className="mr-1.5 text-blue-600" />
                  3. Select Specific User (Drilldown)
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2 text-xs sm:text-sm font-semibold text-slate-900 border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                >
                  <option value="">
                    {selectedAdmin ? `-- All Users of ${selectedAdmin} --` : "-- All System Users --"}
                  </option>
                  {availableUsersList.map((u) => (
                    <option key={u._id} value={u.email}>
                      {u.email} ({u.role})
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {/* Category Dropdown (Dependent) */}
            <div className={`bg-white p-3 rounded-lg border border-slate-200 shadow-2xs ${!isSuper ? "sm:col-span-2" : ""}`}>
              <label className="flex items-center text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                <Layers size={14} className="mr-1.5 text-blue-600" />
                {isSuper ? "4. Select Category" : "2. Select Category"}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={!selectedLanguage}
                className={`w-full px-3 py-2 text-xs sm:text-sm font-semibold text-slate-900 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer ${
                  !selectedLanguage ? "bg-slate-100 cursor-not-allowed opacity-60" : "bg-white"
                }`}
              >
                <option value="">-- Select Category --</option>
                {categories.map((catKey) => {
                  const s = getCategoryStats(catKey);
                  return (
                    <option key={catKey} value={catKey}>
                      {getTranslatedLabel(catKey, "category")} — ({s.uploaded} / {s.total} Uploaded)
                    </option>
                  );
                })}
              </select>
            </div>

          </div>

          {/* Row 3: Voice Control Dropdown (Dependent) */}
          <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
            <label className="flex items-center text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
              <Volume2 size={14} className="mr-1.5 text-blue-600" />
              {isSuper ? "5. Select Voice Control" : "3. Select Voice Control"}
            </label>
            <select
              value={selectedVoiceControl}
              onChange={(e) => setSelectedVoiceControl(e.target.value)}
              disabled={!selectedCategory || !selectedLanguage}
              className={`w-full px-3 py-2 text-xs sm:text-sm font-semibold text-slate-900 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer ${
                !selectedCategory || !selectedLanguage ? "bg-slate-100 cursor-not-allowed opacity-60" : "bg-white"
              }`}
            >
              <option value="">-- Select Voice Control --</option>
              {selectedCategory &&
                tvControlData[selectedCategory] &&
                Object.keys(tvControlData[selectedCategory]).map((vcKey) => {
                  const s = getVoiceControlStats(vcKey);
                  return (
                    <option key={vcKey} value={vcKey}>
                      {getTranslatedLabel(vcKey, "voiceControl")} — ({s.uploaded} / {s.total} Uploaded - {s.pending} Pending)
                    </option>
                  );
                })}
            </select>
          </div>

        </div>

        {/* CONTENT AREA: ONLY DISPLAYED WHEN ALL DROPDOWNS ARE SELECTED */}
        {!selectedLanguage || !selectedCategory || !selectedVoiceControl ? (
          /* PLACEHOLDER INSTRUCTION WHEN DROPDOWNS ARE NOT YET SELECTED */
          <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white text-center">
            <div className="p-4 bg-blue-50 rounded-full text-blue-600 mb-4 border border-blue-100 shadow-2xs">
              <Filter size={36} />
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-1">
              Select Filters to View Tracking Data
            </h3>
            <p className="text-xs text-slate-500 max-w-md leading-relaxed">
              Please select a <strong>Language</strong>, <strong>Category</strong>, and <strong>Voice Control</strong> from the dropdowns above to display tracking numbers and sample utterances.
            </p>
          </div>
        ) : (
          /* MAIN CONTENT DISPLAY WHEN ALL FILTERS ARE SELECTED */
          <>
            {/* OVERVIEW PROGRESS CARD */}
            <div className="px-6 py-4 bg-white border-b border-slate-200 shrink-0">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-blue-50/70 p-4 rounded-xl border border-blue-100">
                <div>
                  <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                    {isSuper && selectedAdmin
                      ? `Admin Progress: ${selectedAdmin}`
                      : "Voice Control Overview"}
                  </span>
                  <h3 className="text-base font-bold text-slate-900 mt-0.5">
                    {getTranslatedLabel(selectedVoiceControl, "voiceControl")}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Category: <strong>{getTranslatedLabel(selectedCategory, "category")}</strong> |
                    {isSuper && selectedAdmin && (
                      <span> Admin: <strong>{selectedAdmin}</strong> |</span>
                    )}
                    <span> User Filter: <strong>{selectedUser || "All Team Users"}</strong></span>
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <p className="text-xs text-slate-500 font-medium">Uploaded</p>
                    <p className="text-lg font-bold text-emerald-600">{currentVcStats.uploaded}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200"></div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 font-medium">Pending</p>
                    <p className="text-lg font-bold text-rose-600">{currentVcStats.pending}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200"></div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 font-medium">Total</p>
                    <p className="text-lg font-bold text-slate-800">{currentVcStats.total}</p>
                  </div>
                  <div className="pl-2">
                    <span className="px-3 py-1 bg-blue-600 text-white rounded-full font-bold text-xs shadow-2xs">
                      {currentVcStats.percent}% Done
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* SAMPLE UTTERANCES LIST */}
            <div className="flex-1 overflow-y-auto p-6 bg-white">
              <div className="mb-3 flex justify-between items-center">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Sample Utterances Status ({currentUtterancesList.length})
                </h4>
                <span className="text-xs text-slate-500">
                  Language Total Progress: <strong>{langOverallStats.uploaded} / {langOverallStats.total} Uploaded</strong>
                </span>
              </div>

              {isFetching ? (
                <div className="flex justify-center items-center h-36 text-slate-500 text-sm">
                  <Loader2 size={18} className="animate-spin mr-2 text-blue-600" />
                  Loading utterances status...
                </div>
              ) : (
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                        <th className="py-2.5 px-3 w-10 text-center">#</th>
                        <th className="py-2.5 px-3">Sample Utterance</th>
                        <th className="py-2.5 px-3 w-32 text-center">Status</th>
                        <th className="py-2.5 px-3">Uploader Account</th>
                        <th className="py-2.5 px-3 w-20 text-center">Audio</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {currentUtterancesList.map((item) => (
                        <tr
                          key={item.id}
                          className={`hover:bg-slate-50 transition-colors ${
                            !item.isUploaded ? "bg-rose-50/20" : "bg-emerald-50/10"
                          }`}
                        >
                          <td className="py-2.5 px-3 text-center text-slate-400 font-mono text-xs">
                            {item.index}
                          </td>
                          <td className="py-2.5 px-3 font-semibold text-slate-900">
                            {item.utterance}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            {item.isUploaded ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">
                                <CheckCircle2 size={12} /> Uploaded
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold bg-rose-100 text-rose-800 rounded-full">
                                <Clock size={12} /> Pending
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-slate-600 text-xs">
                            {item.isUploaded && item.audioData ? (
                              <span className="font-semibold text-slate-800">
                                {item.audioData.uploaderEmail || item.audioData.email || "N/A"}
                              </span>
                            ) : (
                              <span className="text-slate-400 italic">Not submitted yet</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            {item.isUploaded && item.audioData ? (
                              loadingAudioId === item.audioData._id ? (
                                <Loader2 size={14} className="animate-spin mx-auto text-blue-600" />
                              ) : (
                                <button
                                  onClick={() => handlePlayAudio(item.audioData.fileName, item.audioData._id)}
                                  className={`p-1.5 rounded-full text-white transition-colors ${
                                    playingAudioId === item.audioData._id ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"
                                  }`}
                                >
                                  {playingAudioId === item.audioData._id ? <Square size={10} /> : <Play size={10} />}
                                </button>
                              )
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-xs text-slate-500 shrink-0">
          <span>
            {selectedLanguage ? (
              <>
                Language: <strong className="capitalize text-slate-800">{selectedLanguage}</strong> | Progress: <strong>{langOverallStats.uploaded} / {langOverallStats.total} Uploaded</strong>
              </>
            ) : (
              <span>Please select filters to start tracking</span>
            )}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-800 text-white font-semibold rounded-md hover:bg-slate-900 transition-colors"
          >
            Close Tracker
          </button>
        </div>

      </div>
    </div>
  );
}

export default UtteranceTrackerModal;
